import { AppConfigContext } from '@App/AppConfigContext';
import { ExperimentFeatureSets, Experiments, FeatureSet } from '@Config/Experiments';
import { Features } from '@Config/Features';
import { Variants } from '@Config/Variants';
import {
  clearExperimentsFromDev,
  getExperimentsFromDev,
  persistExperimentsForDev,
  toggleDevExperiment
} from '@Framework/experiments';
import {
  clearFeaturesFromDev,
  getFeaturesFromDev,
  persistFeaturesForDev,
  toggleFeature
} from '@Framework/featureFlags';
import { Box, createStyles, makeStyles, Switch, Theme, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { CORPORATE_COLORS } from '@Ui/theme/colors/corporate';
import React, { useContext, useState } from 'react';

import { Button } from '../../atoms/Button/Button';
import { PrimaryAlert } from '../../molecules/PrimaryAlert';

const FeatureOverview = (): React.ReactElement => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { features, experiments } = useContext(AppConfigContext);

  const [shallRerender, setShallRerender] = useState<number>(0);

  const rerender = () => {
    setShallRerender(shallRerender + 1);
  };

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {},
      content: {
        maxWidth: '72em',
        marginRight: 'auto',
        marginLeft: 'auto',
        paddingRight: '1rem',
        paddingLeft: '1rem'
      }
    })
  );

  const classes = useStyles();

  const devFeatures = getFeaturesFromDev();
  const devExperiments = getExperimentsFromDev();
  let experimentsFromDevExist = false;
  if (devExperiments && Object.keys(devExperiments).length > 0) {
    experimentsFromDevExist = true;
  }
  let featuresFromDevExist = false;
  if (devFeatures && Object.keys(devFeatures).length > 0) {
    featuresFromDevExist = true;
  }

  let alertText: any = undefined;
  if (experimentsFromDevExist && featuresFromDevExist) {
    alertText = (
      <>
        Experiments and features were configured via this page.
        <br />
        This can lead to errors!
        <br />
        In doubt clear the storage and activate your desired configration again.
      </>
    );
  }

  return (
    <>
      <Box bgcolor={CORPORATE_COLORS.LIGHT_GRAY} key={shallRerender}>
        <Box display="flex" p={2.5} pt={3.7} component="div" flex="1" className={classes.content}>
          <Box flex={1}>
            <Typography variant="h2">Feature Overview</Typography>
          </Box>
        </Box>
      </Box>

      {alertText && <PrimaryAlert text={alertText} />}

      <Box display="flex" p={2.5} component="div" flex="1" className={classes.content}>
        {!mobile && <Box width="12rem">{/*Placeholder*/}</Box>}
        <Box flex={1}>
          <Button
            color={'primary'}
            variant="contained"
            onClick={() => {
              clearFeaturesFromDev();
              clearExperimentsFromDev();
              setTimeout(() => {
                location.reload();
              }, 50);
            }}
          >
            Clear
          </Button>

          <ul id={'feature-overview-list'}>
            {Object.entries(Features).map((item, index) => {
              return (
                <li key={index}>
                  <label>
                    <strong>{item[0]}</strong>
                    <Switch
                      color={'primary'}
                      checked={features[item[1]] === true}
                      onChange={() => {
                        toggleFeature(item[1]);
                        persistFeaturesForDev();
                        rerender();
                      }}
                    />
                    <input type={'text'} value={`?ft_${item[1]}=1`} readOnly />
                  </label>
                </li>
              );
            })}
          </ul>

          <ul id={'feature-overview-list'}>
            <Typography variant={'subtitle1'}>Experiments</Typography>
            {Object.keys(Experiments).map((exp, index) => {
              const isActive = experiments[Experiments[exp]].active ? 'active' : 'inactive';

              return (
                <li key={index}>
                  <Typography variant={'h2'}>
                    {exp} ({isActive})
                  </Typography>
                  <Typography style={{ marginLeft: '5%' }} variant={'h3'}>
                    Variants
                  </Typography>
                  <ul>
                    {Object.entries<FeatureSet>(ExperimentFeatureSets[Experiments[exp]]).map(
                      ([variant, featureSet], index) => {
                        return (
                          <li key={'y_' + index}>
                            <label
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center'
                              }}
                            >
                              <Switch
                                color={'primary'}
                                checked={
                                  experiments[Experiments[exp]] !== undefined &&
                                  experiments[Experiments[exp]].variant === Variants[variant]
                                }
                                onChange={() => {
                                  toggleDevExperiment(Experiments[exp], Variants[variant]);
                                  persistExperimentsForDev();
                                  rerender();
                                }}
                              />
                              <strong style={{ width: '80%' }}>{variant}</strong>
                            </label>
                            <ul>
                              {Object.entries(featureSet).map(([name, flag], index) => {
                                return (
                                  <li key={'x_' + index}>
                                    <label
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center'
                                      }}
                                    >
                                      <Switch color={'primary'} checked={flag} />
                                      <strong style={{ width: '80%' }}>{name}</strong>
                                    </label>
                                  </li>
                                );
                              })}
                            </ul>
                          </li>
                        );
                      }
                    )}
                  </ul>
                </li>
              );
            })}
          </ul>
        </Box>
      </Box>

      <style
        dangerouslySetInnerHTML={{
          __html: `
          
        #feature-overview-list li label {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
  align-items: stretch;
        }
        #feature-overview-list li label input {
        width: auto;
        margin-left: 20px;
  flex: 1;
        }
          
        #feature-overview-list li label strong {
        width: 100%;
        flex-break: after;
        }
  
          
        #feature-overview-list li {
          list-style: none;
          margin-bottom: 10px;
          margin-top: 4px;
       } 
          
        #ods-client {
          width: 99.5vw;
        }

        html, body {
          overflow-x: hidden;
        }

        .o-CmsContent__ComponentHeading {
          display: none;
        }

        @media screen and (min-width: 45em) {
            .u-padding-x-large--top\\@medium {
                padding-top: 0;
            }
        }
      `
        }}
      />
    </>
  );
};

export { FeatureOverview };
